import { useEffect, useState } from 'react';
import api from '../services/api';
import Input from '../components/Input';
import Select from '../components/Select';
import Button from '../components/Button';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

type FieldType = 'text' | 'date' | 'number' | 'select' | 'checkbox';

interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: { value: string; label: string }[];
}

interface CategoryConfig {
  key: string; // URL path segment under /employees/:id/...
  label: string;
  listKey: string; // key of the array in the GET response
  fields: FieldDef[];
  title: (row: any) => string;
  subtitle?: (row: any) => string;
}

const CATEGORIES: CategoryConfig[] = [
  {
    key: 'education',
    label: 'Education',
    listKey: 'education',
    fields: [
      { key: 'institutionName', label: 'Institution Name', type: 'text', required: true },
      { key: 'qualification', label: 'Qualification', type: 'text' },
      { key: 'fieldOfStudy', label: 'Field of Study', type: 'text' },
      { key: 'startDate', label: 'Start Date', type: 'date' },
      { key: 'endDate', label: 'End Date', type: 'date' },
    ],
    title: (r) => r.institution_name,
    subtitle: (r) => [r.qualification, r.field_of_study].filter(Boolean).join(' — '),
  },
  {
    key: 'experience',
    label: 'Work Experience',
    listKey: 'experience',
    fields: [
      { key: 'companyName', label: 'Company Name', type: 'text', required: true },
      { key: 'jobTitle', label: 'Job Title', type: 'text' },
      { key: 'durationYears', label: 'Duration (Years)', type: 'number' },
      { key: 'responsibilities', label: 'Responsibilities', type: 'text' },
    ],
    title: (r) => r.company_name,
    subtitle: (r) => [r.job_title, r.duration_years ? `${r.duration_years} yr(s)` : null].filter(Boolean).join(' — '),
  },
  {
    key: 'certifications',
    label: 'Certification / License',
    listKey: 'certifications',
    fields: [
      { key: 'certificationName', label: 'Certification / License Name', type: 'text', required: true },
      { key: 'issuingBody', label: 'Issuing Body', type: 'text' },
      { key: 'licenseNumber', label: 'License Number', type: 'text' },
      { key: 'issueDate', label: 'Issue Date', type: 'date' },
      { key: 'expiryDate', label: 'Expiry Date', type: 'date' },
    ],
    title: (r) => r.certification_name,
    subtitle: (r) => [r.issuing_body, r.expiry_date ? `expires ${new Date(r.expiry_date).toLocaleDateString()}` : null].filter(Boolean).join(' — '),
  },
  {
    key: 'professional-affiliations',
    label: 'Professional Affiliations',
    listKey: 'affiliations',
    fields: [
      { key: 'organizationName', label: 'Organization Name', type: 'text', required: true },
      { key: 'membershipType', label: 'Membership Type', type: 'text' },
      { key: 'membershipNumber', label: 'Membership Number', type: 'text' },
      { key: 'memberSince', label: 'Member Since', type: 'date' },
    ],
    title: (r) => r.organization_name,
    subtitle: (r) => [r.membership_type, r.membership_number].filter(Boolean).join(' — '),
  },
  {
    key: 'language-skills',
    label: 'Language Skills',
    listKey: 'languageSkills',
    fields: [
      { key: 'languageName', label: 'Language', type: 'text', required: true },
      {
        key: 'proficiencyLevel',
        label: 'Proficiency Level',
        type: 'select',
        options: [
          { value: 'Beginner', label: 'Beginner' },
          { value: 'Intermediate', label: 'Intermediate' },
          { value: 'Advanced', label: 'Advanced' },
          { value: 'Native', label: 'Native' },
        ],
      },
      { key: 'canRead', label: 'Can Read', type: 'checkbox' },
      { key: 'canWrite', label: 'Can Write', type: 'checkbox' },
      { key: 'canSpeak', label: 'Can Speak', type: 'checkbox' },
    ],
    title: (r) => r.language_name,
    subtitle: (r) =>
      [r.proficiency_level, [r.can_read && 'Read', r.can_write && 'Write', r.can_speak && 'Speak'].filter(Boolean).join('/')]
        .filter(Boolean)
        .join(' — '),
  },
];

const emptyForm = (fields: FieldDef[]) =>
  Object.fromEntries(fields.map((f) => [f.key, f.type === 'checkbox' ? false : '']));

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search
    ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase()))
    : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function EmployeePersonalDetails() {
  const [employeeId, setEmployeeId] = useState('');
  const [employeeLabel, setEmployeeLabel] = useState('');
  const [activeCategory, setActiveCategory] = useState<CategoryConfig>(CATEGORIES[0]);
  const [records, setRecords] = useState<any[]>([]);
  const [form, setForm] = useState<Record<string, any>>(emptyForm(CATEGORIES[0].fields));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm(emptyForm(activeCategory.fields));
    if (employeeId) load(activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, employeeId]);

  const load = async (category: CategoryConfig) => {
    setLoading(true);
    try {
      const res = await api.get(`/employees/${employeeId}/${category.key}`);
      setRecords(res.data[category.listKey] || []);
    } catch (error) {
      console.error(`Failed to fetch ${category.label}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/employees/${employeeId}/${activeCategory.key}`, form);
      setForm(emptyForm(activeCategory.fields));
      load(activeCategory);
    } catch (error: any) {
      alert(error.response?.data?.message || `Failed to add ${activeCategory.label} record`);
    }
  };

  const remove = async (id: string) => {
    await api.delete(`/employees/${employeeId}/${activeCategory.key}/${id}`);
    load(activeCategory);
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Employee Personal Details</h1>

      <div className="bg-white rounded-lg shadow p-6 max-w-md">
        <LookupSelect
          label="Employee"
          placeholder="Search employee by name or code..."
          value={employeeId}
          displayValue={employeeLabel}
          onChange={(value, option) => {
            setEmployeeId(value);
            setEmployeeLabel(option?.label || '');
          }}
          fetchOptions={fetchEmployeeOptions}
        />
      </div>

      {employeeId && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-gray-500 mb-3">Choose data category to add</h2>
          <div className="flex gap-2 flex-wrap mb-6">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setActiveCategory(c)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeCategory.key === c.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mb-2">{activeCategory.label}</h3>
          <form onSubmit={add} className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 items-end">
            {activeCategory.fields.map((f) =>
              f.type === 'checkbox' ? (
                <label key={f.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                  />
                  {f.label}
                </label>
              ) : f.type === 'select' ? (
                <Select
                  key={f.key}
                  value={form[f.key] || ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  options={f.options || []}
                />
              ) : (
                <Input
                  key={f.key}
                  placeholder={f.label}
                  type={f.type === 'number' ? 'number' : f.type}
                  value={form[f.key] || ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  required={f.required}
                />
              )
            )}
            <Button type="submit">+ New</Button>
          </form>

          <ul className="divide-y">
            {records.map((r) => (
              <li key={r.id} className="py-2 flex justify-between items-center text-sm">
                <span>
                  <span className="font-medium">{activeCategory.title(r)}</span>
                  {activeCategory.subtitle && activeCategory.subtitle(r) && (
                    <span className="text-gray-500"> — {activeCategory.subtitle(r)}</span>
                  )}
                </span>
                <button onClick={() => remove(r.id)} className="text-red-600 hover:underline">
                  Remove
                </button>
              </li>
            ))}
            {!loading && records.length === 0 && (
              <p className="text-gray-400 text-sm py-2">No {activeCategory.label.toLowerCase()} on record</p>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
